import styled from "styled-components";

const colors = {
  primary: "#203b34",
  primaryLight: "#f9ae17",
  secondary: "#f9ae17",
  terciary: "#E03A85",
  white: "#ffffff",
  gray: "#6b7280",
  grayLight: "#e5e7eb",
  grayBg: "#f9fafb",
  dark: "#1f2937",
};

export const FormContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
`;

export const Header = styled.div`
  margin-bottom: 20px;
`;

export const Title = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
  margin-top: 0;
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  margin-bottom: 10px;
`;

export const SectionCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const WeightProgressContainer = styled.div`
  margin-bottom: 24px;
`;

export const WeightLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

export const WeightText = styled.span`
  font-size: 14px;
  color: #4b5563;
  font-weight: 500;
`;

export const WeightValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $complete, $error }) =>
    $error ? "#ef4444" : $complete ? "#10b981" : "#3b82f6"};
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: ${({ $complete, $error }) =>
    $error
      ? "linear-gradient(90deg, #ef4444 0%, #dc2626 100%)"
      : $complete
      ? "linear-gradient(90deg, #10b981 0%, #059669 100%)"
      : "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)"};
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
  transition: width 0.3s ease, background 0.3s ease;
  border-radius: 4px;
`;

export const ObjectiveItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`;

export const ObjectiveHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${({ $expanded }) => ($expanded ? "#f9fafb" : "#ffffff")};
  cursor: pointer;
  gap: 12px;
  user-select: none;
`;

export const ObjectiveNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1f2937;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

export const ObjectiveTitle = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ObjectiveValidationIcon = styled.i`
  font-size: 16px;
  color: ${({ $isValid }) => ($isValid ? "#10b981" : "#f59e0b")};
  transition: color 0.2s ease;
  margin-left: 4px;
`;

export const ObjectiveWeight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  padding: 4px 12px;
  background: #f3f4f6;
  border-radius: 12px;
`;

export const ExpandIcon = styled.i`
  font-size: 16px;
  color: #9ca3af;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) =>
    $expanded ? "rotate(180deg)" : "rotate(0deg)"};
`;

export const ObjectiveContent = styled.div`
  padding: ${({ $expanded }) => ($expanded ? "0 16px 16px 16px" : "0")};
  max-height: ${({ $expanded }) => ($expanded ? "1000px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
`;

export const FormGroup = styled.div`
  margin-bottom: 16px;
`;

export const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: #1f2937;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:read-only {
    background: #f9fafb;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const WeightSliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const WeightInput = styled.input`
  width: 70px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:read-only {
    background: #f9fafb;
    cursor: not-allowed;
  }

  /* Remove arrows from number input */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  appearance: textfield;
`;

export const WeightSlider = styled.input`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    transition: background 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::-webkit-slider-thumb:hover {
    background: #2563eb;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: none;
    transition: background 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb:hover {
    background: #2563eb;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const CharacterCount = styled.div`
  font-size: 12px;
  color: ${({ $error }) => ($error ? "#ef4444" : "#9ca3af")};
  text-align: right;
  margin-top: 4px;
`;

export const InfoIcon = styled.i`
  font-size: 16px;
  color: #9ca3af;
  cursor: help;
  transition: color 0.2s ease;

  &:hover {
    color: #6b7280;
  }
`;

export const RequiredBadge = styled.span`
  color: #ef4444;
  font-size: 14px;
  margin-left: 4px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

export const Button = styled.button`
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled(Button)`
  /* background: #3b82f6; */
  color: #ffffff;

  &:hover:not(:disabled) {
    /* background: #2563eb; */
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const SecondaryButton = styled(Button)`
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

export const SkillItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 12px;
  gap: 12px;
`;

export const SkillWeight = styled.div`
  width: 60px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

export const SkillName = styled.div`
  flex: 1;
  font-size: 14px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const EvaluationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $type }) => ($type === "auto" ? "#fef3c7" : "#dbeafe")};
  color: ${({ $type }) => ($type === "auto" ? "#92400e" : "#1e40af")};
`;

export const FinalEvaluationCard = styled.div`
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  background: linear-gradient(135deg, #ffffffe0 0%, #cecece9b 100%);

  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  color: #203b34;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 0.5px solid #203b34;
`;

export const FinalEvaluationLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  opacity: 0.95;
`;

export const FinalEvaluationValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CheckIcon = styled.i`
  font-size: 14px;
  color: #10b981;
`;

// Evaluation Results Styles
export const EvaluationResultsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// export const EvaluationResultCard = styled.div`
//   flex: 0 0 calc(50% - 6px);
//   background: ${({ $type }) =>
//     $type === "auto"
//       ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
//       : "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"};
//   border-radius: 8px;
//   padding: 12px;
//   position: relative;
//   overflow: hidden;
//   transition: all 0.3s ease;
//   border: 1px solid ${({ $type }) => ($type === "auto" ? "#fbbf24" : "#60a5fa")};

//   &:hover {
//     transform: translateY(-2px);
//     box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
//   }

//   @media (max-width: 768px) {
//     flex: 1 1 auto;
//   }
// `;
export const EvaluationResultCard = styled.div`
  flex: 0 0 calc(50% - 6px);
  background: ${({ $type }) =>
    $type === "auto"
      ? "linear-gradient(135deg, #eee 0%, #ddd 100%)"
      : "linear-gradient(135deg, #528f80 0%, #30584e 100%)"};
  border-radius: 8px;
  padding: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid ${({ $type }) => ($type === "auto" ? "#bbb" : "#60a5fa")};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    flex: 1 1 auto;
  }
`;

// export const EvaluationResultCard = styled.div`
//   /* background: ${colors.grayBg};
//   border-radius: 8px;
//   padding: 24px;
//   margin-top: 16px; */
//   flex: 0 0 calc(50% - 6px);
//   background: ${({ $type }) =>
//     $type === "auto"
//       ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
//       : "linear-gradient(135deg, #528f80 0%, #30584e 100%)"};
//   border-radius: 8px;
//   padding: 12px;
//   position: relative;
//   overflow: hidden;
//   transition: all 0.3s ease;
//   border: 1px solid ${({ $type }) => ($type === "auto" ? "#fbbf24" : "#203b34")};

//   &:hover {
//     transform: translateY(-2px);
//     box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
//   }

//   @media (max-width: 768px) {
//     flex: 1 1 auto;
//   }
// `;

export const EvaluationResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const EvaluationResultIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $type }) => ($type === "auto" ? "#f9ae17" : "#203b34")};
  color: #ffffff;
  font-size: 13px;
  flex-shrink: 0;
`;

export const EvaluationResultTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $type }) => ($type === "auto" ? "#222" : "#ffff")};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const EvaluationResultScore = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $type }) => ($type === "auto" ? "#222" : "#fff")};
  margin-bottom: 2px;
`;

export const EvaluationResultLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${({ $type }) => ($type === "auto" ? "#222" : "#fff")};
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
  margin-right: 10px;
`;

export const EvaluationComment = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: ${({ $type }) =>
    $type === "auto" ? "rgba(61, 61, 61, 0.1)" : "rgba(32, 59, 52, 0.5)"};
  border-radius: 6px;
  border-left: 2px solid
    ${({ $type }) => ($type === "auto" ? "#203b34" : "rgb(249, 174, 23)")};
`;

export const EvaluationCommentLabel = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: ${({ $type }) => ($type === "auto" ? "#203b34" : "#fff")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 8px;
  }
`;

export const EvaluationCommentText = styled.div`
  font-size: 11px;
  color: ${({ $type }) => ($type === "auto" ? "#203b34" : "#fff")};
  line-height: 1.4;
  word-break: break-word;
`;

// export const EvaluationResultHeader = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 8px;
//   margin-bottom: 8px;
// `;

// export const EvaluationResultIcon = styled.div`
//   width: 28px;
//   height: 28px;
//   border-radius: 50%;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   background: ${({ $type }) => ($type === "auto" ? "#f59e0b" : "#3b82f6")};
//   color: #ffffff;
//   font-size: 13px;
//   flex-shrink: 0;
// `;

// export const EvaluationResultTitle = styled.div`
//   font-size: 11px;
//   font-weight: 600;
//   color: ${({ $type }) => ($type === "auto" ? "#92400e" : "#1e40af")};
//   text-transform: uppercase;
//   letter-spacing: 0.3px;
// `;

// export const EvaluationResultScore = styled.div`
//   font-size: 18px;
//   font-weight: 700;
//   color: ${({ $type }) => ($type === "auto" ? "#78350f" : "#1e3a8a")};
//   margin-bottom: 2px;
// `;

// export const EvaluationResultLabel = styled.div`
//   font-size: 10px;
//   font-weight: 500;
//   color: ${({ $type }) => ($type === "auto" ? "#92400e" : "#1e40af")};
//   opacity: 0.7;
//   text-transform: uppercase;
//   letter-spacing: 0.3px;
//   margin-bottom: 8px;
// `;

// export const EvaluationComment = styled.div`
//   margin-top: 8px;
//   padding: 8px;
//   background: ${({ $type }) =>
//     $type === "auto" ? "rgba(245, 158, 11, 0.1)" : "rgba(59, 130, 246, 0.1)"};
//   border-radius: 6px;
//   border-left: 2px solid
//     ${({ $type }) => ($type === "auto" ? "#f59e0b" : "#3b82f6")};
// `;

// export const EvaluationCommentLabel = styled.div`
//   font-size: 9px;
//   font-weight: 600;
//   color: ${({ $type }) => ($type === "auto" ? "#92400e" : "#1e40af")};
//   text-transform: uppercase;
//   letter-spacing: 0.5px;
//   margin-bottom: 4px;
//   display: flex;
//   align-items: center;
//   gap: 4px;

//   i {
//     font-size: 8px;
//   }
// `;

// export const EvaluationCommentText = styled.div`
//   font-size: 11px;
//   color: ${({ $type }) => ($type === "auto" ? "#78350f" : "#1e3a8a")};
//   line-height: 1.4;
//   word-break: break-word;
// `;

export const EvaluationCommentButton = styled.button`
  margin-top: 12px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $type }) => ($type === "auto" ? "#92400e" : "#1e40af")};
  background: ${({ $type }) =>
    $type === "auto" ? "rgba(245, 158, 11, 0.2)" : "rgba(59, 130, 246, 0.2)"};
  border: 1px solid
    ${({ $type }) =>
      $type === "auto" ? "rgba(245, 158, 11, 0.3)" : "rgba(59, 130, 246, 0.3)"};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  justify-content: center;

  &:hover {
    background: ${({ $type }) =>
      $type === "auto" ? "rgba(245, 158, 11, 0.3)" : "rgba(59, 130, 246, 0.3)"};
    transform: translateY(-1px);
  }

  i {
    font-size: 10px;
  }
`;

export const NoEvaluationMessage = styled.div`
  flex: 0 0 calc(50% - 6px);
  padding: 12px;
  background: #f3f4f6;
  border-left: 2px solid #9ca3af;
  border-radius: 6px;
  font-size: 11px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    color: #9ca3af;
    font-size: 12px;
  }

  @media (max-width: 768px) {
    flex: 1 1 auto;
  }
`;

export const EvaluationFormContainer = styled.div`
  /* background: ${colors.grayBg};
  border-radius: 8px;
  padding: 24px;
  margin-top: 16px; */
  flex: 0 0 calc(50% - 6px);
  background: ${({ $type }) =>
    $type === "auto"
      ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
      : "linear-gradient(135deg, #528f80 0%, #30584e 100%)"};
  border-radius: 8px;
  padding: 12px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid ${({ $type }) => ($type === "auto" ? "#fbbf24" : "#203b34")};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    flex: 1 1 auto;
  }
`;

export const EvaluationFormResultScore = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $type }) => ($type === "auto" ? "#222" : "#fff")};
  margin-bottom: 2px;
`;

export const EvaluationFormResultLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${({ $type }) => ($type === "auto" ? "#222" : "#fff")};
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
  margin-right: 10px;
`;

export const AssessmentRating = styled.div`
  margin-bottom: 12px;
`;

export const RatingBadge = styled.span`
  background: ${({ $type }) =>
    $type === "auto" ? colors.secondary : colors.primary};
  color: ${colors.white};
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 700;
  display: inline-block;
`;

export const PageHeader = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;

  h2 {
    color: #203b34;
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  p {
    color: #6c757d;
    margin: 0;
    font-size: 14px;
  }
  div {
    margin: 0;
    padding: 0;
    justify-content: left;
  }
  h3 {
    color: #203b34;
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }
`;

export const BreadcrumbDiv = styled.div`
  margin: 0 !important;
  padding: 0 !important;

  .row {
    margin: 0 !important;
    justify-content: flex-start !important;
  }

  .breadcrumb {
    margin: 0 !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .breadcrumb-item {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

export const InfoUser = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const UserTitle = styled.div`
  font-size: 18px;
  color: ${colors.dark};
  font-weight: 600;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const UserName = styled.div`
  font-size: 14px;
  color: ${colors.gray};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FinalEvaluationCardNew = styled.div`
  /* background: linear-gradient(135deg, #ffec7d 0%, #764ba2 100%); */
  background: linear-gradient(135deg, #ffffffe0 0%, #cecece9b 100%);
  /* color: #f9ae17b0; */
  border-radius: 12px;
  padding: 20px 24px;
  /* margin-bottom: 24px; */
  /* color: #203b34; */
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 0.5px solid #203b34;
`;

export const FinalEvaluationValueNew = styled.div`
  font-size: 25px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #203b34;
`;
